<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DatePicker from 'vue-datepicker-next';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { DATE_PICKER_LANG_PT_BR } from 'dashboard/helper/datePickerLang';
import {
  inOneHour,
  tomorrowAt9,
  nextMondayAt9,
  minSchedulable,
  isPastWindow,
} from './scheduleShortcuts';

const props = defineProps({
  initialValue: { type: Date, default: null },
  windowClosesAt: { type: Date, default: null },
  isLoading: { type: Boolean, default: false },
  mode: { type: String, default: 'create' }, // 'create' | 'reschedule'
});
const emit = defineEmits(['confirm', 'close']);
const { t } = useI18n();

const selected = ref(props.initialValue || inOneHour());
watch(
  () => props.initialValue,
  value => {
    if (value) selected.value = value;
  }
);

const shortcuts = computed(() => [
  { key: 'IN_ONE_HOUR', value: inOneHour() },
  { key: 'TOMORROW_9', value: tomorrowAt9() },
  { key: 'MONDAY_9', value: nextMondayAt9() },
]);
const title = computed(() =>
  t(
    `CONVERSATION.SCHEDULED_MESSAGES.${
      props.mode === 'reschedule' ? 'RESCHEDULE_TITLE' : 'TITLE'
    }`
  )
);
const preview = computed(() =>
  selected.value
    ? format(selected.value, "EEEE, d 'de' MMM 'às' HH:mm", { locale: ptBR })
    : ''
);
const tooSoon = computed(
  () => !selected.value || selected.value < minSchedulable()
);
const pastWindow = computed(() =>
  isPastWindow(selected.value, props.windowClosesAt)
);
const disabledDate = date => date < new Date(new Date().setHours(0, 0, 0, 0));
const disabledTime = date => date < minSchedulable();
const confirm = () => {
  if (!tooSoon.value) emit('confirm', selected.value);
};
</script>

<template>
  <div
    class="flex flex-col gap-3 p-4 w-[22rem] rounded-xl border shadow-lg bg-n-solid-2 border-n-strong"
  >
    <div class="flex justify-between items-center">
      <span class="text-sm font-medium text-n-slate-12">{{ title }}</span>
      <NextButton icon="i-lucide-x" ghost slate xs @click="emit('close')" />
    </div>
    <div class="flex flex-wrap gap-2">
      <NextButton
        v-for="shortcut in shortcuts"
        :key="shortcut.key"
        faded
        slate
        xs
        :label="
          t(`CONVERSATION.SCHEDULED_MESSAGES.SHORTCUTS.${shortcut.key}`)
        "
        @click="selected = shortcut.value"
      />
    </div>
    <DatePicker
      v-model:value="selected"
      type="datetime"
      inline
      :minute-step="5"
      :lang="DATE_PICKER_LANG_PT_BR"
      :disabled-date="disabledDate"
      :disabled-time="disabledTime"
    />
    <p v-if="preview" class="text-sm text-n-teal-11">{{ preview }}</p>
    <p v-if="tooSoon" class="text-xs text-n-ruby-11">
      {{ t('CONVERSATION.SCHEDULED_MESSAGES.TOO_SOON') }}
    </p>
    <p v-else-if="pastWindow" class="text-xs text-n-amber-11">
      {{ t('CONVERSATION.SCHEDULED_MESSAGES.WINDOW_WARNING') }}
    </p>
    <div class="flex gap-2 justify-end">
      <NextButton
        faded
        slate
        sm
        :label="t('CONVERSATION.SCHEDULED_MESSAGES.CANCEL')"
        @click="emit('close')"
      />
      <NextButton
        blue
        sm
        :label="t('CONVERSATION.SCHEDULED_MESSAGES.CONFIRM')"
        :disabled="tooSoon || isLoading"
        :is-loading="isLoading"
        @click="confirm"
      />
    </div>
  </div>
</template>
